import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { IGetRulesListData } from "../../types/rules";
import { useToast } from "../../services/utils";
import useFetchDomains from "../../hooks/useFetchDomains";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import RulesListDataTable from "../../components/DataGrids/RulesListDataGrid";
import { GridColDef } from "@mui/x-data-grid";
import AutoCompleteDomainList from "../../components/Molecules/Domain/AutoCompleteDomainList";
import ButtonComponent from "../../components/Button.Component";
import { useAuth } from "../../contexts/AuthContext";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { deleteRule, reactivateRule } from "../../services/rulesService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useFetchPaginatedRules from "../../hooks/useFetchPaginatedRules";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchAllResources from "../../hooks/useFetchAllResources";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  IconAudit,
  IconImportFileWhite,
  IconReportsSvg,
  IconExportIconBlue,
  IconResearchQuerySvg,
  IconEyeBase,
  IconEditBase,
  IconDeleteBlueSvg,
  IconExecuteBase,
  IconReactivateSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import {
  downloadRuleExportFile,
  getCurrentDateAndLast7Days,
  getFormattedDateTime,
} from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import AssociateEntitiesDialog from "../../components/Dialogs/AssociateEntitiesDialog";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import AssignResearchQueryDialog from "../../components/Dialogs/AssignResearchQueryDialog";
import { useAppTranslation } from "../../hooks/useAppTranslation";

// Centralized Filter Configuration for RulesList
const RULES_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    domain_id: null,
    resource_id: null,
    rule_id: null, // Can be single ID or array of IDs (for rule_filter_ids)
    rule_name: "",
    status_filter: "Active", // Default to Active status
    comparison_research_query_available: null, // Boolean filter for rules with comparison research queries (true/false/null)
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["domain_id", "resource_id", "rule_id"],
    STRING_FIELDS: ["rule_name", "status_filter"],
    BOOLEAN_FIELDS: ["comparison_research_query_available"],
    DATE_FIELDS: [],
  },

  // Mapping between internal state field names and URL/API parameter names
  // This handles cases where the internal field name differs from the API parameter name
  URL_PARAM_MAPPING: {
    rule_id: "rule_filter_ids", // Internal: rule_id (array), URL/API: rule_filter_ids (comma-separated)
  } as Record<string, string>,
} as const;

const RulesList = () => {
  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { domainId } = useParams();
  const { t } = useAppTranslation();

  // URL search params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track if we're updating from URL changes to prevent circular updates
  const isUpdatingFromURL = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isResourceDataLoading, setIsResourceDataLoading] =
    useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Permission checks for rule actions
  const canViewRule = hasPermission("rule", "view");
  const canAddRule = hasPermission("rule", "add");
  const canEditRule = hasPermission("rule", "edit");
  const canDeleteRule = hasPermission("rule", "delete");
  const canViewRuleResult = hasPermission("rule_result", "view");
  const canViewRuleResearchQuery = hasPermission(
    "comparison_research_query",
    "view"
  );
  const canExecuteRule = hasPermission("rule", "execute");
  const [openAssociateEntitiesDialog, setOpenAssociateEntitiesDialog] =
    useState<boolean>(false);
  const [associateEntityId, setAssociateEntityId] = useState<number | null>(
    null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<any>(null);
  // Assign Research Query Dialog state
  const [openAssignResearchQueryDialog, setOpenAssignResearchQueryDialog] =
    useState<boolean>(false);
  const [selectedRuleForResearchQuery, setSelectedRuleForResearchQuery] =
    useState<{ id: number; name: string } | null>(null);
  const handleAssociateEntitiesDialog = () => {
    setOpenAssociateEntitiesDialog(true);
  };
  // Handle opening Assign Research Query dialog
  const handleOpenAssignResearchQueryDialog = (rule: any) => {
    setSelectedRuleForResearchQuery({ id: rule.id, name: rule.name });
    setOpenAssignResearchQueryDialog(true);
  };
  // Handle closing Assign Research Query dialog
  const handleCloseAssignResearchQueryDialog = () => {
    setOpenAssignResearchQueryDialog(false);
    setSelectedRuleForResearchQuery(null);
  };

  // Handle delete rule
  const handleDeleteRule = (rule: any) => {
    setRuleToDelete(rule);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteRule(ruleToDelete.id);
      if (result && result.status === 200) {
        showToast(
          t("rules.toast.deleted", { name: ruleToDelete.name }),
          "success"
        );
        setDeleteConfirmOpen(false);
        setRuleToDelete(null);
        // Update the rules list to remove the deleted rule
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== ruleToDelete.id
          ),
        }));
      }
    } catch (error: any) {
      showToast(error?.message || t("rules.toast.failedDelete"), "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate rule
  const handleReactivate = async (id: number, ruleName: string) => {
    setIsLoading(true);
    try {
      await reactivateRule(id);
      showToast(t("rules.toast.reactivated", { name: ruleName }), "success");

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));

      // Refetch the list to update the UI with fresh data from database
      // The list will be refetched automatically due to searchFilterData dependency
    } catch (error) {
      showToast(t("rules.toast.failedReactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const [domainsData] = useFetchDomains({ setIsLoading });
  const [
    domainAutocompleteInitialOptions,
    setDomainAutocompleteInitialOptions,
  ] = useState<{ label: string; value: string }[]>([]);
  const [domainAutocompleteOptions, setDomainAutocompleteOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [domainselected, setDomainselected] = useState<any>();
  const [currentDomainId, setCurrentDomainId] = useState<any>(
    Number(domainId) || ""
  );

  // Helper function to get initial filter state from URL
  const getInitialFilterState = () => {
    // Use centralized default state
    const defaultFilterState = { ...RULES_FILTER_CONFIG.DEFAULT_STATE };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Handle ID fields (convert to number if present, or handle comma-separated IDs)
    RULES_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach((field) => {
      // Check if this field has a URL parameter mapping
      const urlParamName =
        RULES_FILTER_CONFIG.URL_PARAM_MAPPING[field] || field;
      const value = searchParams.get(urlParamName);

      if (value) {
        // Special handling for rule_filter_ids (comma-separated IDs)
        if (urlParamName === "rule_filter_ids") {
          // Convert comma-separated string to array of numbers
          const ids = value
            .split(",")
            .map((id) => Number(id.trim()))
            .filter((id) => !isNaN(id));
          if (ids.length > 0) {
            urlFilters[field] = ids;
          }
        } else if (!isNaN(Number(value))) {
          // Single ID field
          urlFilters[field] = Number(value);
        }
      }
    });

    // Handle string fields
    RULES_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        urlFilters[field] = value;
      }
    });

    // Handle boolean fields
    RULES_FILTER_CONFIG.FIELD_TYPES.BOOLEAN_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value !== null) {
        // Convert string "true"/"false" to boolean
        urlFilters[field] = value === "true";
      }
    });

    return { ...defaultFilterState, ...urlFilters };
  };

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = (filters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Use centralized field configuration
    const fieldsToSerialize = RULES_FILTER_CONFIG.URL_FIELDS;

    // Remove all existing filter parameters first (using URL parameter names)
    fieldsToSerialize.forEach((field) => {
      const urlParamName =
        RULES_FILTER_CONFIG.URL_PARAM_MAPPING[field] || field;
      newParams.delete(urlParamName);
    });

    // Add non-empty filter values to URL (using URL parameter names)
    fieldsToSerialize.forEach((field) => {
      const value = filters[field];
      if (value !== null && value !== undefined && value !== "") {
        // Get the URL parameter name (may differ from internal field name)
        const urlParamName =
          RULES_FILTER_CONFIG.URL_PARAM_MAPPING[field] || field;

        // Special handling for rule_filter_ids (array to comma-separated string)
        if (urlParamName === "rule_filter_ids" && Array.isArray(value)) {
          const idsString = value.join(",");
          newParams.set(urlParamName, idsString);
        } else if (typeof value === "boolean") {
          // Boolean fields
          newParams.set(urlParamName, String(value));
        } else if (typeof value === "string") {
          // String fields
          newParams.set(urlParamName, value);
        } else if (Array.isArray(value)) {
          // Other array fields (shouldn't happen, but handle gracefully)
          newParams.set(urlParamName, value.join(","));
        } else {
          // Number fields
          newParams.set(urlParamName, String(value));
        }
      }
    });

    return newParams;
  };

  // Helper function to clear filters (state only, URL will be updated by useEffect)
  const clearFilters = useCallback(() => {
    // Reset filter state to default - URL will be updated automatically by the filter useEffect
    setSearchFilterData({ ...RULES_FILTER_CONFIG.DEFAULT_STATE });
  }, []);

  const [searchFilterData, setSearchFilterData] = useState<any>(() =>
    getInitialFilterState()
  );

  // Extract status filter for use in useEffect dependency
  const statusFilter = searchFilterData?.status_filter || "Active";

  // Convert status filter to isActive parameter and prepare API data
  // Use useMemo to create a stable reference and prevent infinite loops
  const apiSearchFilterData = useMemo(() => {
    const isActiveParam =
      statusFilter === "All" ? undefined : statusFilter === "Active";

    // Prepare search filter data for API (exclude status_filter as it's handled separately)
    const apiData = { ...searchFilterData };
    delete apiData.status_filter;

    // Add isActive to the API filter data
    if (isActiveParam !== undefined) {
      apiData.is_active = isActiveParam;
    }

    // Apply field name mapping for API parameters
    // Handle rule_id → rule_filter_ids mapping
    if (apiData.rule_id !== null && apiData.rule_id !== undefined) {
      if (Array.isArray(apiData.rule_id)) {
        // Convert array to comma-separated string for API
        apiData.rule_filter_ids = apiData.rule_id.join(",");
      } else {
        // Single ID - convert to comma-separated string with single value
        apiData.rule_filter_ids = String(apiData.rule_id);
      }
      delete apiData.rule_id; // Remove internal field name
    }

    return apiData;
  }, [searchFilterData, statusFilter]);

  const [ruleslistData] = useFetchPaginatedRules({
    currentDomainId,
    setIsLoading,
    page,
    pSize,
    searchFilterData: apiSearchFilterData,
  });

  const [resourcesData] = useFetchAllResources({
    currentDomainId,
    setIsLoading: setIsResourceDataLoading,
  });
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [isFileListModalOpen, setIsFileListModalOpen] = useState(false);
  const [fileListModalData, setFileListModalData] =
    useState<IGetRulesListData | null>(null);
  const [expandedRow, setExpandedRow] = useState<any>([]);

  // Update URL when filter state changes (only when not updating from URL)
  useEffect(() => {
    // Skip URL update if we're currently updating from URL changes
    if (isUpdatingFromURL.current) {
      return;
    }

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(searchFilterData);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams);
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (for back/forward navigation)
  useEffect(() => {
    // Skip if we're currently updating from URL changes to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Extract current filter values from URL
    const urlFilterState = getInitialFilterState();

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Update filter state if URL has different values
    if (hasFilterChanges) {
      // Set flag to prevent URL updates while we update state
      isUpdatingFromURL.current = true;

      setSearchFilterData(urlFilterState);

      // Reset flag after state update is processed
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 100);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Memoize searchFilterData updates to prevent unnecessary re-renders
  const updateSearchFilter = useCallback((newData: any) => {
    setSearchFilterData(newData);
  }, []);

  // Reset page to 1 when status filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    const new_options = domainsData.map((domainItem: any) => {
      return {
        label: domainItem.domain_name,
        value: `${domainItem.id}`,
      };
    });
    setDomainAutocompleteOptions(new_options);
    setDomainAutocompleteInitialOptions(new_options);
  }, [domainsData]);

  useEffect(() => {
    setDomainselected(
      domainAutocompleteInitialOptions.find((dacio) => dacio.value === domainId)
    );
  }, [domainAutocompleteInitialOptions]);

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: (params: any) => (
        <IconButton
          size="small"
          tabIndex={-1}
          onClick={() => {
            if (expandedRow.includes(params.row.id)) {
              setExpandedRow((prev: any) =>
                prev.filter((item: any) => item !== params.row.id)
              );
            } else {
              setExpandedRow((prev: any) => [...prev, params.row.id]);
            }
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: `rotateZ(${
                expandedRow.includes(params.row.id) ? 180 : 0
              }deg)`,
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
            fontSize="inherit"
          />
        </IconButton>
      ),
    },
    {
      field: "id",
      headerName: t("rules.list.columns.id"),
      minWidth: 70,
      renderCell: (params: any) => {
        return <span style={{ cursor: "pointer" }}>{params.id}</span>;
      },
    },
    {
      field: "name",
      headerName: t("rules.list.columns.rules"),
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "domain_name",
      headerName: t("rules.list.columns.domain"),
      minWidth: 140,
      flex: 1,
      renderCell: (params) => (
        <LimitChractersWithTooltip value={params?.value} />
      ),
    },
    {
      field: "resources",
      headerName: t("rules.list.columns.resources"),
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const resourceNames =
          params.row.rule_schema?.resources?.map((rItem: number) => {
            const resourceMatched = resourcesData.find(
              (resourceItem: any) => resourceItem.id === rItem
            );
            return resourceMatched?.resource_name;
          }) || [];
        const resourceString = resourceNames.join(", ");
        return (
          <>
            {resourceNames.length > 0 && (
              <LimitChractersWithTooltip value={resourceString} />
            )}
          </>
        );
      },
    },
    {
      field: "description",
      headerName: t("rules.list.columns.description"),
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },

    {
      field: "modified_by_user",
      headerName: t("rules.list.columns.modifiedBy"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: t("rules.list.columns.lastModified"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("rules.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title={t("common.status.active")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={t("common.status.inactive")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: t("rules.list.columns.action"),
      align: "center",
      headerAlign: "center",
      width: 160,
      renderCell: (params: any) => {
        const isActive = params.row.is_active === true;
        const handleDelete = () => {
          handleDeleteRule(params.row);
        };
        const { last7DaysDate } = getCurrentDateAndLast7Days();

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                {canExecuteRule && (
                  <Tooltip
                    title={t("rules.tooltip.ruleExecution")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(
                          `/rules/${domainId}/rule-execution/${params.row.id}`
                        );
                      }}
                      aria-label="Execute rule"
                      data-testid={`execute-rule-${params.row.id}-btn`}
                    >
                      <IconExecuteBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canViewRule && (
                  <Tooltip
                    title={t("rules.tooltip.viewRule")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/rules/${domainId}/view/${params.row.id}`);
                      }}
                      aria-label="View rule"
                      data-testid={`view-rule-${params.row.id}-btn`}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditRule && (
                  <Tooltip
                    title={t("rules.tooltip.editRule")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/rules/${domainId}/edit/${params.row.id}`);
                      }}
                      aria-label="Edit rule"
                      data-testid={`edit-rule-${params.row.id}-btn`}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    // Recent comparisons - requires rule result view permission
                    ...(canViewRuleResult
                      ? [
                          <IconButton
                            key="recent-comparisons"
                            onClick={() => {
                              navigate(
                                `/rules-execution-history?ruleId=${params.row.id}&fromDate=${last7DaysDate}`
                              );
                            }}
                            size="small"
                            className="datagrid-action-btn"
                          >
                            <IconReportsSvg />
                            {t("rules.dropdown.recentComparisons")}
                          </IconButton>,
                        ]
                      : []),
                    // Audit Rule - requires rule view permission
                    ...(canViewRule
                      ? [
                          <IconButton
                            key="audit-rule"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() =>
                              navigate(
                                `/rules/${domainId}/auditRule/${params.row.id}`
                              )
                            }
                          >
                            <IconAudit />
                            {t("rules.dropdown.auditRule")}
                          </IconButton>,
                        ]
                      : []),
                    // Export - requires rule view permission
                    ...(canViewRule
                      ? [
                          <IconButton
                            key="export"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => {
                              setAssociateEntityId(params.row.id);
                              handleAssociateEntitiesDialog();
                            }}
                          >
                            <IconExportIconBlue />
                            {t("rules.dropdown.export")}
                          </IconButton>,
                        ]
                      : []),
                    // Delete Rule - requires rule delete permission
                    ...(canDeleteRule
                      ? [
                          <IconButton
                            key="delete-rule"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete()}
                          >
                            <IconDeleteBlueSvg />
                            {t("rules.dropdown.deleteRule")}
                          </IconButton>,
                        ]
                      : []),
                    // Manage Research Query - requires comparison research query view permission
                    ...(canViewRuleResearchQuery
                      ? [
                          <IconButton
                            key="assign-research-query"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() =>
                              navigate(
                                `/comparison-research-queries/view/${params.row.id}`
                              )
                            }
                          >
                            <IconResearchQuerySvg />
                            {t("rules.dropdown.manageResearchQuery")}
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && canEditRule && (
              <Tooltip
                title={t("rules.tooltip.reactivateRule")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const showFileListModal = (row: IGetRulesListData) => {
    setFileListModalData(row);
    setIsFileListModalOpen(true);
  };

  const handelOnChangeDomain = (event: any, value: any) => {
    if (value?.id !== undefined) {
      setCurrentDomainId(value?.id);
      setDomainselected(value?.id);
      navigate(`/rules-list/${value?.id}`);
      setPage(defaultPage ? parseInt(defaultPage) : 1);
      setPSize(rowsPerPage);
    } else {
      setFileData(paginatedResponseFormat);
      setCurrentDomainId(null);
      navigate(`/rules-list/all`);
    }
  };
  const handelClickEvent = () => {
    navigate("/rules/add");
  };

  // for update rule list data
  useEffect(() => {
    if (ruleslistData) {
      // setIsLoading(true);
      setFileData(ruleslistData);
      // setIsLoading(false);
    }
  }, [ruleslistData]);

  return (
    <Box>
      {(isLoading || isDownloadLoading || isResourceDataLoading) && (
        <Loader
          isLoading={isLoading || isDownloadLoading || isResourceDataLoading}
        />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("rules.dialog.deleteTitle")}
        dailogDescription={t("common.messages.confirmDelete")}
      />

      {/* import  button component */}
      {/* import autocomplete list of domain */}
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={updateSearchFilter}
              searchFilterData={searchFilterData}
              requiredFields={[
                "domain_list",
                "resource_list",
                "rule_list",
                "status_filter",
                "comparison_research_query_available",
              ]}
              primaryFields={["domain_list", "resource_list"]}
              fieldRequirements={{
                dateTitle: "Date",
                tooltipRequired: true,
                includeDomainId: true,
                includeRuleId: true,
                includeResourceId: true,
                enableDomainResourceCascading: true, // ✅ Enable cascading filter from domain to resource
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
                hasMultipleRuleIds: true, // ✅ Enable multiple rule selection for rule_filter_ids
              }}
            />
          </Grid>
          {canAddRule && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start", pt: "8px" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                {t("rules.list.title")}
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* import data table for resource list  */}
      <FlexBetween style={{ paddingTop: "20px" }}>
        <RulesListDataTable
          dataRows={fileData?.items || []}
          dataColumns={columns || []}
          loading={isLoading}
          dataListTitle={t("rules.list.title")}
          isExportButtonRequired={true}
          className="dataTable no-radius pt-0 bdr-top-0 hide-progress-icon"
          disableColumnFilter={true}
          paginationMode="server"
          rowCount={fileData?.total || 0}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={320}
        />
      </FlexBetween>
      <AssociateEntitiesDialog
        openAssociateEntitiesDialog={openAssociateEntitiesDialog}
        setOpenAssociateEntitiesDialog={setOpenAssociateEntitiesDialog}
        associateEntityId={associateEntityId}
        downloadExportFile={downloadRuleExportFile}
        setIsDownloadLoading={setIsDownloadLoading}
        header={"Rule"}
      />
      <AssignResearchQueryDialog
        open={openAssignResearchQueryDialog}
        onClose={handleCloseAssignResearchQueryDialog}
        ruleData={selectedRuleForResearchQuery}
        showToast={showToast}
      />
    </Box>
  );
};

export default RulesList;
